import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/userStore'

export const appAuthRoutes: Array<RouteRecordRaw> = [
  // Dashboards
  {
    path: 'dashboard',
    name: 'Dashboard',
    meta: {
      requiresAuth: true,
    },
    component: () => import('@/views/dashboards/DashboardView.vue'),
  },
  // Orders
  {
    path: 'orders',
    name: 'Orders',
    meta: {
      requiresAuth: true,
      permission: 'orders.view',
    },
    component: () => import('@/views/orders/OrdersView.vue'),
    children: [
      {
        path: ':orderID',
        name: 'Order',
        meta: { requiresAuth: true },
        component: () => import('@/views/orders/OrderView.vue'),
        // Without a child match no tab is active (the command center and the dashboard
        // widgets link to the bare `Order` route). Params carry over to the redirect.
        redirect: { name: 'OrderGeneral' },
        children: [
          {
            path: 'general',
            name: 'OrderGeneral',
            meta: { requiresAuth: true },
            component: () => import('@/views/orders/OrderGeneralView.vue'),
          },
          {
            path: 'samples',
            name: 'OrderSamples',
            meta: { requiresAuth: true },
            component: () => import('@/views/orders/OrderSamplesView.vue'),
          },
          {
            path: 'deliveries',
            name: 'OrderDeliveries',
            meta: { requiresAuth: true },
            component: () => import('@/views/orders/OrderDeliveriesView.vue'),
          },
          {
            path: 'invoice',
            name: 'OrderInvoice',
            meta: { requiresAuth: true },
            component: () => import('@/views/orders/OrderInvoiceView.vue'),
          },
          {
            path: 'samples/:sampleID',
            name: 'OrderSample',
            meta: { requiresAuth: true },
            component: () => import('@/views/orders/OrderSampleView.vue'),
          },
          {
            path: 'activity',
            redirect: { name: 'OrderGeneral' },
          },
        ],
      },
    ],
  },
  {
    path: 'orders/analyses',
    name: 'OrdersAnalyses',
    meta: {
      requiresAuth: true,
      permission: 'orders.view',
    },
    component: () => import('@/views/orders/OrdersAnalysesView.vue'),
  },
  {
    path: 'orders/deliveries',
    name: 'OrdersDeliveries',
    meta: {
      requiresAuth: true,
      permission: 'orders.view',
    },
    component: () => import('@/views/orders/OrdersDeliveriesView.vue'),
  },
  // Customers
  {
    path: 'customers',
    name: 'Customers',
    meta: {
      requiresAuth: true,
      permission: 'customers.view',
    },
    component: () => import('@/views/customerAccounts/CustomersView.vue'),
    redirect: { name: 'CustomerAccountsList' },
    children: [
      {
        path: 'accounts',
        name: 'CustomerAccountsList',
        meta: {
          requiresAuth: true,
          permission: 'customers.view',
        },
        component: () =>
          import('@/views/customerAccounts/CustomerAccountsView.vue'),
        children: [
          {
            path: ':customerAccountID',
            name: 'CustomerAccount',
            meta: {
              requiresAuth: true,
              permission: 'customers.view',
            },
            component: () =>
              import('@/views/customerAccounts/CustomerAccountView.vue'),
            children: [
              {
                path: 'general',
                name: 'CustomerEditGeneral',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerAccountEditGeneralView.vue'
                  ),
              },
              {
                path: 'drop-points',
                name: 'CustomerEditDropPoints',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerAccountEditDropPointsView.vue'
                  ),
                children: [
                  {
                    path: ':privateDropPointID/info',
                    name: 'CustomerEditPrivateDropPointInfo',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/PrivateDropPointEditInfoView.vue'
                      ),
                  },
                ],
              },
              {
                path: 'users',
                name: 'CustomerEditUsers',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerAccountEditUsersView.vue'
                  ),
                children: [
                  {
                    path: ':customerUserID/details',
                    name: 'CustomerUserEditDetails',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerUserBusinessEditDetailsView.vue'
                      ),
                  },
                  {
                    path: ':customerUserID/roles',
                    name: 'CustomerUserEditRoles',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.manage',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerUserBusinessEditRolesView.vue'
                      ),
                  },
                ],
              },
              {
                path: 'pricing',
                name: 'CustomerEditPricing',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerAccountEditPricingView.vue'
                  ),
                redirect: (to) => ({
                  name: 'CustomerEditPricingDetails',
                  params: to.params,
                }),
                children: [
                  {
                    path: 'details',
                    name: 'CustomerEditPricingDetails',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditDetailsView.vue'
                      ),
                  },
                  {
                    path: 'analyses',
                    name: 'CustomerEditPricingAnalyses',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'CustomerEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditAnalysesView.vue'
                      ),
                  },
                  {
                    path: 'tats',
                    name: 'CustomerEditPricingTats',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'CustomerEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditTatsView.vue'
                      ),
                  },
                  {
                    path: 'addons',
                    name: 'CustomerEditPricingAddons',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'CustomerEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditAddonsView.vue'
                      ),
                  },
                  {
                    path: 'deals',
                    name: 'CustomerEditPricingDeals',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'CustomerEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditDealsView.vue'
                      ),
                  },
                ],
              },
              {
                path: 'billing',
                name: 'CustomerEditBilling',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerAccountEditBillingView.vue'
                  ),
              },
              {
                path: 'insurance-customers',
                name: 'CustomerEditInsuranceCustomerAccounts',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerAccountEditInsuranceCustomerAccountsView.vue'
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'private',
        name: 'CustomerPrivateUsers',
        meta: {
          requiresAuth: true,
          permission: 'customers.view',
        },
        component: () =>
          import('@/views/customerAccounts/CustomerUsersPrivateView.vue'),
        children: [
          {
            path: ':privateUserID',
            name: 'CustomerUserPrivate',
            meta: {
              requiresAuth: true,
              permission: 'customers.view',
            },
            component: () =>
              import('@/views/customerAccounts/CustomerUserPrivateView.vue'),
            children: [
              {
                path: 'general',
                name: 'CustomerPrivateUserEditGeneral',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerUserPrivateEditGeneralView.vue'
                  ),
              },
              {
                path: 'pricing',
                name: 'CustomerPrivateUserEditPricing',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerUserPrivateEditPricingView.vue'
                  ),
                redirect: (to) => ({
                  name: 'CustomerPrivateUserEditPricingDetails',
                  params: to.params,
                }),
                children: [
                  {
                    path: 'details',
                    name: 'CustomerPrivateUserEditPricingDetails',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditDetailsView.vue'
                      ),
                  },
                  {
                    path: 'analyses',
                    name: 'CustomerPrivateUserEditPricingAnalyses',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'CustomerPrivateUserEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditAnalysesView.vue'
                      ),
                  },
                  {
                    path: 'tats',
                    name: 'CustomerPrivateUserEditPricingTats',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'CustomerPrivateUserEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditTatsView.vue'
                      ),
                  },
                  {
                    path: 'addons',
                    name: 'CustomerPrivateUserEditPricingAddons',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'CustomerPrivateUserEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditAddonsView.vue'
                      ),
                  },
                  {
                    path: 'deals',
                    name: 'CustomerPrivateUserEditPricingDeals',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'CustomerPrivateUserEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditDealsView.vue'
                      ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'insurance',
        name: 'InsuranceCustomerAccountsList',
        meta: {
          requiresAuth: true,
          permission: 'customers.view',
        },
        component: () => import('@/views/customerAccounts/InsuranceCustomerAccountsView.vue'),
        children: [
          {
            path: ':insuranceCustomerAccountID',
            name: 'InsuranceCustomerAccount',
            meta: {
              requiresAuth: true,
              permission: 'customers.view',
            },
            component: () => import('@/views/customerAccounts/InsuranceCustomerAccountView.vue'),
            children: [
              {
                path: 'general',
                name: 'InsuranceCustomerAccountEditGeneral',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import('@/views/customerAccounts/InsuranceCustomerAccountEditGeneralView.vue'),
              },
              {
                path: 'pricing',
                name: 'InsuranceCustomerAccountEditPricing',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import('@/views/customerAccounts/InsuranceCustomerAccountEditPricingView.vue'),
                redirect: (to) => ({
                  name: 'InsuranceCustomerAccountEditPricingDetails',
                  params: to.params,
                }),
                children: [
                  {
                    path: 'details',
                    name: 'InsuranceCustomerAccountEditPricingDetails',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditDetailsView.vue'
                      ),
                  },
                  {
                    path: 'analyses',
                    name: 'InsuranceCustomerAccountEditPricingAnalyses',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'InsuranceCustomerAccountEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditAnalysesView.vue'
                      ),
                  },
                  {
                    path: 'tats',
                    name: 'InsuranceCustomerAccountEditPricingTats',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'InsuranceCustomerAccountEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditTatsView.vue'
                      ),
                  },
                  {
                    path: 'addons',
                    name: 'InsuranceCustomerAccountEditPricingAddons',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'InsuranceCustomerAccountEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditAddonsView.vue'
                      ),
                  },
                  {
                    path: 'deals',
                    name: 'InsuranceCustomerAccountEditPricingDeals',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    // Editing requires customers.manage — customers.view alone is
                    // Detaljer-only, read-only. Redirects to that sibling rather
                    // than the generic App fallback, since the customer's pricing
                    // is still viewable, just not editable here. Mirrors the
                    // pricing segment editor's own guard.
                    beforeEnter: (to) => {
                      if (useUserStore().canUser('customers.manage')) return true
                      return { name: 'InsuranceCustomerAccountEditPricingDetails', params: to.params }
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerPricingEditDealsView.vue'
                      ),
                  },
                ],
              },
              {
                path: 'business-customers',
                name: 'InsuranceCustomerAccountEditBusinessCustomers',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/InsuranceCustomerAccountEditBusinessCustomersView.vue'
                  ),
              },
              // MIL-402 — the business customer's own Brugere / Drop points views, shared through
              // `customerOwner` (see InsuranceCustomerAccountView). Same paths and permissions as
              // the business routes above.
              {
                path: 'users',
                name: 'InsuranceCustomerAccountEditUsers',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerAccountEditUsersView.vue'
                  ),
                children: [
                  {
                    path: ':customerUserID/details',
                    name: 'InsuranceCustomerAccountUserEditDetails',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerUserBusinessEditDetailsView.vue'
                      ),
                  },
                  {
                    path: ':customerUserID/roles',
                    name: 'InsuranceCustomerAccountUserEditRoles',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.manage',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/CustomerUserBusinessEditRolesView.vue'
                      ),
                  },
                ],
              },
              {
                path: 'drop-points',
                name: 'InsuranceCustomerAccountEditDropPoints',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/CustomerAccountEditDropPointsView.vue'
                  ),
                children: [
                  {
                    path: ':privateDropPointID/info',
                    name: 'InsuranceCustomerAccountEditPrivateDropPointInfo',
                    meta: {
                      requiresAuth: true,
                      permission: 'customers.view',
                    },
                    component: () =>
                      import(
                        '@/views/customerAccounts/PrivateDropPointEditInfoView.vue'
                      ),
                  },
                ],
              },
              {
                path: 'billing',
                name: 'InsuranceCustomerAccountEditBilling',
                meta: {
                  requiresAuth: true,
                  permission: 'customers.view',
                },
                component: () =>
                  import(
                    '@/views/customerAccounts/InsuranceCustomerAccountEditBillingView.vue'
                  ),
              },
            ],
          },
        ],
      },
    ],
  },
  // Invoices
  {
    path: 'invoices',
    name: 'Invoices',
    meta: { requiresAuth: true, permission: 'orders.billing' },
    component: () => import('@/views/invoices/InvoicesView.vue'),
  },
  // Settings (includes catalog, team, report drill-downs — all keep the Settings sidebar)
  {
    path: 'settings',
    name: 'Settings',
    meta: { requiresAuth: true },
    component: () => import('@/views/settings/SettingsView.vue'),
    // No route-level `redirect` here on purpose — see the "Settings default-child
    // guard" in router/index.ts for the first-accessible-child logic and why it
    // has to live in a `beforeEach` guard instead.
    children: [
      {
        path: 'collections',
        name: 'SettingsReportDescriptions',
        meta: { requiresAuth: true, permission: 'collections.view' },
        component: () => import('@/views/settings/ReportDescriptionsView.vue'),
      },
      // Sibling of SettingsReportDescriptions, not nested under it —
      // ReportDescriptionsView.vue (the list) has no <router-view> of its
      // own, so a route nested under it has nowhere to render: the URL
      // changes but the list stays on screen. This mirrors the route shape
      // that already worked before Phase 1 moved Collections here (it was a
      // sibling of the list under SettingsReports too), just re-based onto
      // Settings directly.
      {
        path: 'collections/:reportDescriptionID',
        name: 'ReportDescription',
        // Sibling of SettingsReportDescriptions, so it does not inherit its
        // permission — repeat it here or a user without collections.view could
        // navigate straight to this URL and its child item/edit-info routes.
        meta: { requiresAuth: true, permission: 'collections.view' },
        component: () => import('@/views/settings/ReportDescriptionView.vue'),
        children: [
          {
            path: ':reportDescriptionItemID',
            name: 'ReportDescriptionItemEdit',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/settings/ReportDescriptionItemView.vue'),
          },
          {
            path: 'edit',
            name: 'ReportDescriptionEditInfo',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/settings/ReportDescriptionEditInfoView.vue'),
          },
        ],
      },
      {
        path: 'closed-days',
        name: 'SettingsClosedDays',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () => import('@/views/settings/ClosedDaysView.vue'),
        children: [
          {
            path: ':closedDayID/details',
            name: 'ClosedDayEditDetails',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/settings/ClosedDayEditDetailsView.vue'),
          },
        ],
      },
      {
        path: 'tats',
        name: 'SettingsTats',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () => import('@/views/analysisSetup/TatsView.vue'),
        children: [
          {
            path: ':tatID/details',
            name: 'TatEditDetails',
            meta: { requiresAuth: true },
            component: () => import('@/views/tats/TatEditDetailsView.vue'),
          },
        ],
      },
      {
        path: 'priority-colors',
        name: 'SettingsPriorityColors',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () => import('@/views/settings/SettingsPriorityColorsView.vue'),
      },
      {
        path: 'lab-drop-points',
        name: 'SettingsLabDropPoints',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () => import('@/views/settings/DropPointsListView.vue'),
        children: [
          {
            path: ':dropPointID/details',
            name: 'DropPointEditDetails',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/settings/DropPointEditDetailsView.vue'),
          },
        ],
      },
      {
        path: 'bc-integration',
        name: 'SettingsBcIntegration',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () => import('@/views/settings/BcSettingsView.vue'),
        children: [
          {
            // The connection wizard, as a full-screen modal over the settings page. A route of
            // its own because UiFullScreenModal closes by navigating to `closePath` — there is no
            // close event to listen for. Needs setup.manage, not the parent's setup.view.
            path: 'connect',
            name: 'SettingsBcIntegrationConnect',
            meta: { requiresAuth: true, permission: 'setup.manage' },
            component: () => import('@/views/settings/BcConnectionSetupView.vue'),
          },
        ],
      },
      {
        path: 'integrations',
        children: [
          {
            path: '',
            name: 'SettingsIntegrations',
            meta: { requiresAuth: true, permission: 'integrations.manage' },
            component: () =>
              import('@/views/settings/integrations/IntegrationsView.vue'),
          },
          {
            path: 'dhl',
            name: 'IntegrationsSettingsDHL',
            meta: { requiresAuth: true, permission: 'integrations.manage' },
            component: () =>
              import('@/views/settings/integrations/DhlSettingsView.vue'),
          },
        ],
      },
      {
        path: 'system/general',
        name: 'SettingsSystemGeneral',
        meta: { requiresAuth: true, permission: 'settingsGeneral.view' },
        component: () => import('@/views/settings/system/SettingsSystemGeneralView.vue'),
      },
      {
        path: 'system/branding',
        name: 'SettingsSystemBranding',
        meta: { requiresAuth: true, permission: 'settingsBranding.view' },
        component: () => import('@/views/settings/system/SettingsSystemBrandingView.vue'),
      },
      // Addon Services
      {
        path: 'addon-services',
        name: 'AddonServices',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () =>
          import('@/views/addonServices/AddonServicesView.vue'),
        children: [
          {
            path: ':addonServiceID/details',
            name: 'AddonServiceEditDetails',
            meta: { requiresAuth: true },
            component: () =>
              import(
                '@/views/addonServices/AddonServiceEditDetailsView.vue'
              ),
          },
        ],
      },
      // Pricing Segments
      {
        path: 'pricing-segments',
        name: 'PricingSegments',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () => import('@/views/pricingSegments/PricingSegmentsView.vue'),
      },
      {
        path: 'pricing-segments/:segmentID',
        name: 'PricingSegmentEdit',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () =>
          import('@/views/pricingSegments/PricingSegmentEditView.vue'),
        redirect: (to) => ({
          name: 'PricingSegmentEditGeneral',
          params: to.params,
        }),
        children: [
          {
            path: 'general',
            name: 'PricingSegmentEditGeneral',
            meta: { requiresAuth: true, permission: 'setup.view' },
            component: () =>
              import(
                '@/views/pricingSegments/PricingSegmentEditGeneralView.vue'
              ),
          },
          {
            path: 'analyses',
            name: 'PricingSegmentEditAnalyses',
            meta: { requiresAuth: true, permission: 'setup.view' },
            // Full edit (this tab) requires setup.manage — setup.view alone is
            // Generelt-only, read-only. Redirects to the sibling Generelt tab
            // rather than the generic App fallback the permissions guard would
            // otherwise use, since the segment is still viewable, just not here.
            beforeEnter: (to) => {
              if (useUserStore().canUser('setup.manage')) return true
              return { name: 'PricingSegmentEditGeneral', params: to.params }
            },
            component: () =>
              import(
                '@/views/pricingSegments/PricingSegmentEditAnalysesView.vue'
              ),
          },
          {
            path: 'tats',
            name: 'PricingSegmentEditTats',
            meta: { requiresAuth: true, permission: 'setup.view' },
            // Same setup.manage escalation as the Analyser tab above.
            beforeEnter: (to) => {
              if (useUserStore().canUser('setup.manage')) return true
              return { name: 'PricingSegmentEditGeneral', params: to.params }
            },
            component: () =>
              import('@/views/pricingSegments/PricingSegmentEditTatsView.vue'),
          },
          {
            path: 'addons',
            name: 'PricingSegmentEditAddons',
            meta: { requiresAuth: true, permission: 'setup.view' },
            // Same setup.manage escalation as the Analyser/Svartider tabs above.
            beforeEnter: (to) => {
              if (useUserStore().canUser('setup.manage')) return true
              return { name: 'PricingSegmentEditGeneral', params: to.params }
            },
            component: () =>
              import(
                '@/views/pricingSegments/PricingSegmentEditAddonsView.vue'
              ),
          },
          {
            path: 'deals',
            name: 'PricingSegmentEditDeals',
            meta: { requiresAuth: true, permission: 'setup.view' },
            // Same setup.manage escalation as the Analyser/Svartider/Addons tabs above.
            beforeEnter: (to) => {
              if (useUserStore().canUser('setup.manage')) return true
              return { name: 'PricingSegmentEditGeneral', params: to.params }
            },
            component: () =>
              import('@/views/pricingSegments/PricingSegmentEditDealsView.vue'),
          },
        ],
      },
      // Employees
      {
        path: 'employees',
        name: 'Employees',
        meta: {
          requiresAuth: true,
          permission: 'employees.view',
        },
        component: () => import('@/views/employees/EmployeesListView.vue'),
        children: [
          {
            path: ':userID/details',
            name: 'EmployeeEditDetails',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/employees/EmployeeEditDetailsView.vue'),
          },
          {
            path: ':userID/roles',
            name: 'EmployeeEditRoles',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/employees/EmployeeEditRolesView.vue'),
          },
          {
            path: ':userID/pin-code',
            name: 'EmployeeEditPinCode',
            meta: { requiresAuth: true },
            component: () => import('@/components/EmployeeEditPinCodeView.vue'),
          },
        ],
      },
      // Lab Equipment
      {
        path: 'lab-equipment',
        name: 'LabEquipment',
        meta: {
          requiresAuth: true,
          permission: 'equipment.view',
        },
        component: () => import('@/views/labEquipment/LabEquipmentView.vue'),
        children: [
          {
            path: ':labEquipmentID/details',
            name: 'LabEquipmentEdit',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/labEquipment/LabEquipmentEditDetailsView.vue'),
          },
        ],
      },
      {
        path: 'lab-equipment/service-history',
        name: 'LabEquipmentMaintenance',
        meta: {
          requiresAuth: true,
          permission: 'equipment.view',
        },
        component: () =>
          import('@/views/labEquipment/LabEquipmentMaintenanceView.vue'),
        children: [
          {
            path: ':labEquipmentMaintenanceID/edit',
            name: 'LabEquipmentMaintenanceEdit',
            meta: {
              requiresAuth: true,
              permission: 'equipment.view',
            },
            component: () =>
              import(
                '@/views/labEquipment/LabEquipmentMaintenanceEditView.vue'
              ),
          },
        ],
      },
      // Displays
      {
        path: 'displays',
        name: 'SettingsDisplays',
        meta: { requiresAuth: true, permission: 'displays.manage' },
        component: () => import('@/views/settings/DisplaysView.vue'),
      },
      {
        path: 'displays/:displayID',
        name: 'Display',
        meta: { requiresAuth: true, permission: 'displays.manage' },
        component: () => import('@/views/settings/DisplayEditorView.vue'),
      },
      // Lab Locations
      {
        path: 'lab-locations',
        name: 'LabLocations',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () => import('@/views/labLocations/LabLocationsView.vue'),
        children: [
          {
            path: ':labLocationID/details',
            name: 'LabLocationEditDetails',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/labLocations/LabLocationEditDetailsView.vue'),
          },
          {
            path: ':labLocationID/employees',
            name: 'LabLocationEditEmployees',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/labLocations/LabLocationEditEmployeesView.vue'),
          },
        ],
      },
      // Bilag (moved out of the consolidated Reports settings area into the
      // Bibliotek section, 2026-09-17 — route name kept for continuity)
      {
        path: 'bilag',
        name: 'SettingsReportAttachments',
        meta: { requiresAuth: true, permission: 'reportTemplates.view' },
        component: () => import('@/views/settings/ReportAttachmentsView.vue'),
        children: [
          {
            path: ':bilagLibraryItemID/details',
            name: 'BilagLibraryItemEdit',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/settings/BilagLibraryItemEditDetailsView.vue'),
          },
        ],
      },
      // Labels Library
      {
        path: 'labels-library',
        name: 'LabelsLibrary',
        meta: { requiresAuth: true, permission: 'setup.view' },
        component: () => import('@/views/settings/LabelsLibraryView.vue'),
        redirect: { name: 'LabelsLibraryResults' },
        children: [
          {
            path: 'results',
            name: 'LabelsLibraryResults',
            meta: { requiresAuth: true },
            component: () =>
              import(
                '@/views/settings/labelsLibrary/LabelsLibraryResultsView.vue'
              ),
            children: [
              {
                path: 'items/:itemID/info',
                name: 'LabelsLibraryResultsItemInfo',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    '@/views/settings/labelsLibrary/LabelsLibraryItemInfoView.vue'
                  ),
              },
            ],
          },
          {
            path: 'units',
            name: 'LabelsLibraryUnits',
            meta: { requiresAuth: true },
            component: () =>
              import(
                '@/views/settings/labelsLibrary/LabelsLibraryUnitsView.vue'
              ),
            children: [
              {
                path: 'items/:itemID/info',
                name: 'LabelsLibraryUnitsItemInfo',
                meta: { requiresAuth: true },
                component: () =>
                  import(
                    '@/views/settings/labelsLibrary/LabelsLibraryItemInfoView.vue'
                  ),
              },
            ],
          },
        ],
      },
      // Analysis Setup
    ],
  },
  // Dev demo area
  {
    path: 'dev',
    name: 'Dev',
    meta: { requiresAuth: true, requiresDeveloper: true },
    component: () => import('@/views/dev/DevView.vue'),
    redirect: { name: 'DevReportBasicFormatting' },
    children: [
      {
        path: 'reports/basic-formatting',
        name: 'DevReportBasicFormatting',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportBasicFormattingView.vue'),
      },
      {
        path: 'reports/combined-formatting',
        name: 'DevReportCombinedFormatting',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportCombinedFormattingView.vue'),
      },
      {
        path: 'reports/long-content',
        name: 'DevReportLongContent',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportLongContentView.vue'),
      },
      {
        path: 'reports/page-boundary',
        name: 'DevReportPageBoundary',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportPageBoundaryView.vue'),
      },
      {
        path: 'reports/heading-orphan',
        name: 'DevReportHeadingOrphan',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportHeadingOrphanView.vue'),
      },
      {
        path: 'reports/list-spanning-pages',
        name: 'DevReportListSpanningPages',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportListSpanningPagesView.vue'),
      },
      {
        path: 'reports/plain-text-init',
        name: 'DevReportPlainTextInit',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportPlainTextInitView.vue'),
      },
      {
        path: 'reports/empty-content',
        name: 'DevReportEmptyContent',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportEmptyContentView.vue'),
      },
      {
        path: 'reports/pagination',
        name: 'DevReportPagination',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/dev/reports/DevReportPaginationView.vue'),
      },
      {
        path: 'reports/table',
        name: 'DevReportTable',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () => import('@/views/dev/reports/DevReportTableView.vue'),
      },
    ],
  },
  // Sample Areas (new Analysis Setup nav — separate top-level route, mirrors Lab Translations)
  {
    path: 'sample-areas',
    name: 'SampleAreas',
    meta: { requiresAuth: true, permission: 'setup.view' },
    component: () => import('@/views/sampleAreas/SampleAreasNavView.vue'),
    children: [
      {
        path: ':sampleAreaID',
        name: 'SampleArea',
        meta: { requiresAuth: true },
        component: () => import('@/views/sampleAreas/SampleAreaView.vue'),
      },
      {
        path: ':sampleAreaID/focus',
        name: 'SampleAreaFocus',
        meta: { requiresAuth: true },
        component: () => import('@/views/sampleAreas/SampleAreaFocusView.vue'),
        children: [
          {
            path: 'analyses/:analysisID',
            name: 'AnalysisFocus',
            meta: { requiresAuth: true },
            component: () => import('@/views/sampleAreas/AnalysisFocusContentView.vue'),
            children: [
              {
                path: 'analysis-tests/:analysisTestID',
                name: 'AnalysisTestFocus',
                meta: { requiresAuth: true },
                component: () => import('@/views/sampleAreas/AnalysisTestFocusView.vue'),
              },
            ],
          },
        ],
      },
      // Global (lab-wide) list pages — siblings of :sampleAreaID/:sampleAreaID/focus,
      // rendered by the same SampleAreaFocusView.vue shell so navigating between a
      // Sample Area's focus mode and these pages swaps content without closing the
      // modal. See Plans/Kasper/Analysis Setup — Global List Pages.
      {
        path: 'analyses',
        name: 'GlobalAnalyses',
        meta: { requiresAuth: true, isGlobalSampleAreasContent: true },
        component: () => import('@/views/sampleAreas/SampleAreaFocusView.vue'),
        children: [
          {
            path: ':analysisID/details',
            name: 'GlobalAnalysisEditDetails',
            meta: { requiresAuth: true },
            component: () => import('@/views/analysisSetup/AnalysisEditDetailsView.vue'),
          },
          {
            path: ':analysisID/analysis-tests',
            name: 'GlobalAnalysisEditAnalysisTests',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/analysisSetup/AnalysisEditAnalysisTestsView.vue'),
          },
          {
            path: ':analysisID/report-description',
            name: 'GlobalAnalysisEditReportDescription',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/analysisSetup/AnalysisEditReportDescriptionView.vue'),
          },
        ],
      },
      {
        path: 'analysis-tests',
        name: 'GlobalAnalysisTests',
        meta: { requiresAuth: true, isGlobalSampleAreasContent: true },
        component: () => import('@/views/sampleAreas/SampleAreaFocusView.vue'),
        children: [
          {
            path: ':analysisTestID/details',
            name: 'GlobalAnalysisTestEditDetails',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/analysisTests/AnalysisTestEditDetailsView.vue'),
          },
          {
            path: ':analysisTestID/analyses',
            name: 'GlobalAnalysisTestEditAnalyses',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/analysisTests/AnalysisTestEditAnalysesView.vue'),
          },
          {
            path: ':analysisTestID/classification',
            name: 'GlobalAnalysisTestEditClassification',
            meta: { requiresAuth: true },
            component: () =>
              import('@/views/analysisTests/AnalysisTestEditColorsView.vue'),
          },
        ],
      },
    ],
  },
  // Lab Translations
  {
    path: 'lab-translations',
    name: 'LabTranslations',
    meta: {
      requiresAuth: true,
      // Satisfied by `translations.manage` (Owner/Admin) or the directly
      // granted `translations.view` (Lab Assistant). Merged into every
      // child route's meta, so the whole section is gated from here.
      permission: 'translations.view',
    },
    component: () => import('@/views/labTranslations/LabTranslationsView.vue'),
    redirect: { name: 'LabTranslationsLabEquipment' },
    children: [
      {
        path: 'analysis',
        name: 'LabTranslationsAnalysis',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsAnalysisView.vue'),
      },
      {
        path: 'sample-area',
        name: 'LabTranslationsSampleArea',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsSampleAreaView.vue'),
      },
      {
        path: 'analysis-test',
        name: 'LabTranslationsAnalysisTest',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsAnalysisTestView.vue'),
      },
      {
        path: 'tat',
        name: 'LabTranslationsTat',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsTatView.vue'),
      },
      {
        path: 'addon-service',
        name: 'LabTranslationsAddonService',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsAddonServiceView.vue'),
      },
      {
        path: 'lab-equipment',
        name: 'LabTranslationsLabEquipment',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsLabEquipmentView.vue'),
      },
      {
        path: 'pricing-segments',
        name: 'LabTranslationsPricingSegments',
        meta: { requiresAuth: true },
        component: () =>
          import(
            '@/views/labTranslations/LabTranslationsPricingSegmentsView.vue'
          ),
      },
      {
        path: 'drop-point',
        name: 'LabTranslationsDropPoint',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsDropPointView.vue'),
      },
      {
        path: 'closed-day',
        name: 'LabTranslationsClosedDay',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsClosedDayView.vue'),
      },
      {
        path: 'report-description',
        name: 'LabTranslationsReportDescription',
        meta: { requiresAuth: true },
        component: () =>
          import(
            '@/views/labTranslations/LabTranslationsReportDescriptionView.vue'
          ),
      },
      {
        path: 'system-list/:systemKey',
        name: 'LabTranslationsSystemList',
        meta: { requiresAuth: true },
        component: () =>
          import('@/views/labTranslations/LabTranslationsTextListView.vue'),
      },
    ],
  },

  // Reports (separate top-level route, mirrors Lab Translations)
  {
    path: 'reports',
    name: 'SettingsReports',
    meta: { requiresAuth: true },
    component: () => import('@/views/settings/SettingsReportsView.vue'),
    // Redirects to Report Templates — every child route in this subtree
    // requires `reportTemplates.view`, so a role without it is bounced
    // through to the `App` fallback by the permissions guard regardless.
    redirect: { name: 'SettingsReportTemplates' },
    children: [
      {
        path: 'report-settings',
        name: 'SettingsReportSettings',
        meta: { requiresAuth: true, permission: 'reportTemplates.view' },
        component: () => import('@/views/settings/ReportSettingsView.vue'),
      },
      {
        path: 'report-templates',
        name: 'SettingsReportTemplates',
        meta: {
          requiresAuth: true,
          permission: 'reportTemplates.view',
        },
        component: () => import('@/views/settings/ReportTemplatesView.vue'),
      },
      {
        path: 'report-templates/:reportTemplateID',
        name: 'ReportTemplate',
        meta: {
          requiresAuth: true,
          permission: 'reportTemplates.view',
        },
        component: () => import('@/views/settings/ReportTemplateEditorView.vue'),
      },
    ],
  },

  // Design System (developer only)
  {
    path: 'design-system',
    name: 'DesignSystemDocs',
    meta: { requiresAuth: true, requiresDeveloper: true },
    component: () => import('@/views/designSystem/DesignSystemLayoutView.vue'),
    redirect: { name: 'DSComponents' },
    children: [
      {
        path: '',
        name: 'DSOverview',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemOverviewView.vue'),
      },
      {
        path: 'tokens',
        name: 'DSTokens',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemTokensView.vue'),
      },
      {
        path: 'components',
        name: 'DSComponents',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemComponentsView.vue'),
      },
      {
        path: 'components/tab-nav',
        name: 'DSCompTabNav',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompTabNavView.vue'),
      },
      {
        path: 'components/button',
        name: 'DSCompButton',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompButtonView.vue'),
      },
      {
        path: 'components/input-text',
        name: 'DSCompInputText',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInputTextView.vue'),
      },
      {
        path: 'components/input-number',
        name: 'DSCompInputNumber',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInputNumberView.vue'),
      },
      {
        path: 'components/input-select',
        name: 'DSCompInputSelect',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInputSelectView.vue'),
      },
      {
        path: 'components/input-checkbox',
        name: 'DSCompInputCheckbox',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInputCheckboxView.vue'),
      },
      {
        path: 'components/input-radio',
        name: 'DSCompInputRadio',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInputRadioView.vue'),
      },
      {
        path: 'components/combobox',
        name: 'DSCompCombobox',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompComboboxView.vue'),
      },
      {
        path: 'components/input-date',
        name: 'DSCompInputDate',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInputDateView.vue'),
      },
      {
        path: 'components/input-date-range',
        name: 'DSCompInputDateRange',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInputDateRangeView.vue'),
      },
      {
        path: 'components/input-phone',
        name: 'DSCompInputPhone',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInputPhoneView.vue'),
      },
      {
        path: 'components/pin-input',
        name: 'DSCompPinInput',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompPinInputView.vue'),
      },
      {
        path: 'components/icon-button',
        name: 'DSCompIconButton',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompIconButtonView.vue'),
      },
      {
        path: 'components/image-upload',
        name: 'DSCompImageUpload',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompImageUploadView.vue'),
      },
      {
        path: 'components/lang-select',
        name: 'DSCompLangSelect',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompLangSelectView.vue'),
      },
      {
        path: 'components/card',
        name: 'DSCompCard',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompCardView.vue'),
      },
      {
        path: 'components/data-table',
        name: 'DSCompDataTable',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompDataTableView.vue'),
      },
      {
        path: 'components/status-chip',
        name: 'DSCompStatusChip',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompStatusChipView.vue'),
      },
      {
        path: 'components/monitor',
        name: 'DSCompMonitor',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompMonitorView.vue'),
      },
      {
        path: 'components/overlay',
        name: 'DSCompOverlay',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompOverlayView.vue'),
      },
      {
        path: 'components/confirmation-modal',
        name: 'DSCompConfirmationModal',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import(
            '@/views/designSystem/DesignSystemCompConfirmationModalView.vue'
          ),
      },
      {
        path: 'components/toast',
        name: 'DSCompToast',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompToastView.vue'),
      },
      {
        path: 'components/section-card',
        name: 'DSCompSectionCard',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompSectionCardView.vue'),
      },
      {
        path: 'components/info-text',
        name: 'DSCompInfoText',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompInfoTextView.vue'),
      },
      {
        path: 'components/tooltip',
        name: 'DSCompTooltip',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompTooltipView.vue'),
      },
      {
        path: 'components/icon-radio-tabs',
        name: 'DSCompIconRadioTabs',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompIconRadioTabsView.vue'),
      },
      {
        path: 'components/radio-tabs',
        name: 'DSCompRadioTabs',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompRadioTabsView.vue'),
      },
      {
        path: 'components/pill-nav',
        name: 'DSCompPillNav',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompPillNavView.vue'),
      },
      {
        path: 'components/side-pane',
        name: 'DSCompSidePane',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompSidePaneView.vue'),
      },
      {
        path: 'components/full-screen-modal',
        name: 'DSCompFullScreenModal',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import(
            '@/views/designSystem/DesignSystemCompFullScreenModalView.vue'
          ),
        children: [
          {
            path: 'demo/general',
            name: 'DSCompFullScreenModalDemoGeneral',
            meta: { requiresAuth: true, requiresDeveloper: true },
            component: () =>
              import(
                '@/views/designSystem/DesignSystemCompFullScreenModalDemoGeneralView.vue'
              ),
          },
          {
            path: 'demo/pricing',
            name: 'DSCompFullScreenModalDemoPricing',
            meta: { requiresAuth: true, requiresDeveloper: true },
            component: () =>
              import(
                '@/views/designSystem/DesignSystemCompFullScreenModalDemoPricingView.vue'
              ),
          },
          {
            path: 'demo/users',
            name: 'DSCompFullScreenModalDemoUsers',
            meta: { requiresAuth: true, requiresDeveloper: true },
            component: () =>
              import(
                '@/views/designSystem/DesignSystemCompFullScreenModalDemoUsersView.vue'
              ),
            children: [
              {
                path: ':demoUserID',
                name: 'DSCompFullScreenModalDemoUserDetail',
                meta: { requiresAuth: true, requiresDeveloper: true },
                component: () =>
                  import(
                    '@/views/designSystem/DesignSystemCompFullScreenModalDemoUserDetailView.vue'
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'components/editable-list',
        name: 'DSCompEditableList',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompEditableListView.vue'),
      },
      {
        path: 'components/file-upload',
        name: 'DSCompFileUpload',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompFileUploadView.vue'),
      },
      {
        path: 'components/switch',
        name: 'DSCompSwitch',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompSwitchView.vue'),
      },
      {
        path: 'components/icons',
        name: 'DSCompIcons',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemCompIconsView.vue'),
      },
      {
        path: 'demos',
        name: 'DSDemos',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemDemosView.vue'),
      },
      {
        path: 'demos/user-signup',
        name: 'DSDemoUserSignup',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemDemoUserSignupView.vue'),
      },
      {
        path: 'demos/kitchen-sink',
        name: 'DSDemoKitchenSink',
        meta: { requiresAuth: true, requiresDeveloper: true },
        component: () =>
          import('@/views/designSystem/DesignSystemDemoKitchenSinkView.vue'),
      },
    ],
  },
]

